export type ImageQualityIssue = 'blurry' | 'greyscale' | 'lowResolution';

const MIN_SHORT_SIDE_PX = 600;
const ANALYSIS_MAX_DIMENSION = 512;
const COLORED_PIXEL_CHANNEL_SPREAD = 18;
const MIN_COLORED_PIXEL_RATIO = 0.02;
const MIN_LAPLACIAN_VARIANCE = 70;

const isRasterImage = (file: File) => file.type.split('/')[0] === 'image';

const loadImage = async (objectUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = objectUrl;
  });

const drawDownscaled = (image: HTMLImageElement) => {
  const scale = Math.min(
    1,
    ANALYSIS_MAX_DIMENSION / Math.max(image.naturalWidth, image.naturalHeight)
  );
  const width = Math.max(1, Math.round(image.naturalWidth * scale));
  const height = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, width, height);
  return {
    width,
    height,
    data: context.getImageData(0, 0, width, height).data,
  };
};

const isGreyscale = (data: Uint8ClampedArray) => {
  let colored = 0;
  const pixelCount = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const spread = Math.max(r, g, b) - Math.min(r, g, b);
    if (spread > COLORED_PIXEL_CHANNEL_SPREAD) colored += 1;
  }

  return colored / pixelCount < MIN_COLORED_PIXEL_RATIO;
};

const laplacianVariance = (
  data: Uint8ClampedArray,
  width: number,
  height: number
) => {
  const luma = new Float32Array(width * height);
  for (let p = 0; p < luma.length; p += 1) {
    const i = p * 4;
    luma[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = y * width + x;
      const value =
        4 * luma[p] -
        luma[p - 1] -
        luma[p + 1] -
        luma[p - width] -
        luma[p + width];
      sum += value;
      sumSquares += value * value;
      count += 1;
    }
  }

  if (count === 0) return Infinity;
  const mean = sum / count;
  return sumSquares / count - mean * mean;
};

export const analyzeImageQuality = async (
  file: File
): Promise<ImageQualityIssue[]> => {
  if (!isRasterImage(file) || typeof document === 'undefined') return [];

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const issues: ImageQualityIssue[] = [];

    if (Math.min(image.naturalWidth, image.naturalHeight) < MIN_SHORT_SIDE_PX) {
      issues.push('lowResolution');
    }

    const pixels = drawDownscaled(image);
    if (pixels) {
      if (isGreyscale(pixels.data)) issues.push('greyscale');
      if (
        laplacianVariance(pixels.data, pixels.width, pixels.height) <
        MIN_LAPLACIAN_VARIANCE
      ) {
        issues.push('blurry');
      }
    }

    return issues;
  } catch {
    return [];
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
